<template>
  <div>
    <va-modal
      :value="isOpen"
      id="bulkUpdateAppointmentsModal"
      ref="bulkUpdateAppointmentsModal"
      size="medium"
      :showOkay="true"
      :ok-disabled="selectedEntityActivityUser === null"
      :show-cancel="true"
      :cancelText="$t('modals.back')"
      @cancel="onCancel"
      @ok="handleShowConfirmationModal"
      :ok-text="$t('modals.buttons.confirm')"
    >

      <div slot="header">
        <span class="modal-title">{{ $t('clinical.appointments.bulk_transfer_appointments') }}</span>
      </div>

      <div class="evollu-form">
        <br/>
        <div class="row">
          <span>{{
              $t('clinical.appointments.bulk_transfer_appointments_from_to', {
                count: appointments.length,
                start: startDate.format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD')
              })
            }} </span>
        </div>
        <div class="form-row">
          <div class="col">
            <div>
              <label class="form-label">{{ $t('clinical.appointments.facility') }}:</label>
              <multiselect
                v-model="form.data.facility"
                :options="facilitiesOptions"
                track-by="id"
                label="name"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
              >
                <template slot="clear">
                <span class="multiselect__clear" v-if="form.data.facility"
                      @mousedown.prevent.stop="form.data.facility = null"></span>
                </template>
              </multiselect>
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="col">
            <div>
              <label class="form-label">{{ $t('clinical.appointments.activity') }}:</label>
              <multiselect
                v-model="form.data.activity"
                :options="activityOptions"
                track-by="id"
                label="name"
                select-label=""
                selected-label=""
                deselect-label=""
                :customLabel="option => $t(`clinical.appointments.activity_types.${option.id}`, option.name)"
                placeholder="---"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                :disabled="form.data.facility === null || isLoading"
                >
                <template slot="clear">
                <span class="multiselect__clear" v-if="form.data.activity"
                      @mousedown.prevent.stop="form.data.activity = null"></span>
                </template>
              </multiselect>
            </div>
          </div>
        </div>

        <div class="text-center" v-if="isLoading">
          <LoadingAnimation></LoadingAnimation>
        </div>

        <fade-transition :duration="350" mode="out-in">
          <section v-if="entityActivityUserOptions.length && this.form.data.facility !== null" key="activities-list">
            <div class="label-info">
              <span>
                {{ $t('entities.activities.revision.replace_body') }}{{ $t('entities.activities.revision.replace_body_operation_update') }}
              </span>
            </div>

            <div class="list">
              <div
                v-for="(_entityActivityUser, index) in entityActivityUserOptions"
                :key="_entityActivityUser.id">

                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex justify-content-start align-items-center">
                    <va-radio-button
                      :option="_entityActivityUser"
                      v-model="selectedEntityActivityUser"
                      :label="' '"
                    />

                    <EntityActivityUserInfo
                      class="mt-2"
                      :entityActivityUser="_entityActivityUser"
                      @click.prevent="select(_entityActivityUser)"
                    />
                  </div>

                </div>

                <hr v-if="index !== writeEntityActivityUsers.length - 1">
              </div>
            </div>
          </section>
          <section v-else-if="form.data.facility !== null && form.data.activity !== null && !writeEntityActivityUsers.length">
            <div class="d-flex justify-content-center mt-5 label-info">
              <span>{{ $t('entities.activities.revision.no_replace_found') }}</span>
            </div>
          </section>
        </fade-transition>

        <div v-if="unmatchedAppointmentsAfterActivityEnd > 0" class="text-danger text-center">
          <br/>
          <span>{{ $t('clinical.appointments.unmatched_appointments_after_activity_end', {count: unmatchedAppointmentsAfterActivityEnd}) }}</span>
        </div>
        <div v-if="unmatchedAppointmentsBeforeActivityStart > 0" class="text-danger text-center">
          <br/>
          <span>{{ $t('clinical.appointments.unmatched_appointments_before_activity_start', {count: unmatchedAppointmentsBeforeActivityStart}) }}</span>
        </div>
        <br/>
      </div>
    </va-modal>

    <ConfirmationModal
      v-if="showConfirmationModal"
      :title="$t('clinical.appointments.bulk_transfer_appointments')"
      :body="$t('clinical.appointments.bulk_transfer_appointments_confirm', {count: appointments.length,
        dispenser: selectedEntityActivityUser.user ? selectedEntityActivityUser.user.nickname ? `${selectedEntityActivityUser.user.name} (${selectedEntityActivityUser.user.nickname})` : selectedEntityActivityUser.user.name : $t('clinical.appointments.undefined_user'),
        entity: selectedEntityActivityUser.entity.name})"
      @modal-confirmed="onConfirmationModalConfirmed"
      @modal-canceled="onConfirmationModalCanceled"/>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import { ActivityService } from '@/api/AuthService'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'
import { uniqBy, flatten } from 'lodash'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import EntityActivityUserInfo from '@/components/entity-activity-user/EntityActivityUserInfo'
import { AppointmentService } from '@/api/AuthClinicalService'
import ConfirmationModal from '@/components/common/ConfirmationModal'

const adminAccessType = 3
const appointmentsInfoType = 200

export default {
  props: {
    selectedFacilities: {
      type: Array,
      required: true
    },
    selectedUsers: {
      type: Array,
      required: true
    },
    selectedActivities: {
      type: Array,
      required: true
    },
    appointments: {
      type: Array,
      required: true
    },
    startDate: {
      type: Object,
      required: true
    },
    endDate: {
      type: Object,
      required: true
    },
    component: {
      type: Object,
      required: true
    }
  },
  components: {
    Multiselect,
    FadeTransition,
    LoadingAnimation,
    ConfirmationModal,
    EntityActivityUserInfo
  },
  computed: {
    ...mapState('auth', ['activities']),
    authClinicalService: function () {
      return this.component.service
    },
    adminableAppointmentEntities: function () {
      if (this.authClinicalService !== null) {
        const permissions = this.authClinicalService.user_permissions.filter(permission => permission.access_type >= adminAccessType && permission.info_type === appointmentsInfoType)
        if (permissions.some(p => p.entity_id === null)) {
          return [...this.authClinicalService.entities]
        } else {
          return this.authClinicalService.entities.filter(entity => permissions.some(p => p.entity_id === entity.id))
        }
      }
    },
    facilitiesOptions: function () {
      return uniqBy(flatten(this.writeEntityActivityUsers.map(entityActivityUser => {
        return entityActivityUser.entity.facilities
      })), 'id')
    },
    activityOptions: function () {
      if (!this.form.data.facility) {
        return []
      }

      const activities = this.writeEntityActivityUsers
        .filter(entityActivityUser => {
          return entityActivityUser.entity.facilities.some(facility => facility.id === this.form.data.facility.id)
        })
        .map(activityUser => {
          return {
            ...activityUser.activity,
            name: this.$t(`clinical.appointments.activity_types.${activityUser.activity.id}`, activityUser.activity.name)
          }
        })
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

      return uniqBy(activities, 'id')
    },
    entityActivityUserOptions: function () {
      let entityActivityUserOptions = [...this.writeEntityActivityUsers]

      if (this.form.data.facility !== null) {
        entityActivityUserOptions = entityActivityUserOptions.filter(entityActivityUser => {
          return entityActivityUser.entity.facilities.some(facility => facility.id === this.form.data.facility.id)
        })
      }

      if (this.form.data.activity !== null) {
        entityActivityUserOptions = entityActivityUserOptions.filter(entityActivityUser => {
          return entityActivityUser.activity.id === this.form.data.activity.id
        })
      }

      return entityActivityUserOptions
    }
  },
  data: function () {
    return {
      isOpen: false,
      isLoading: false,
      showConfirmationModal: false,
      writeEntityActivityUsers: [],
      unmatchedAppointmentsAfterActivityEnd: 0,
      unmatchedAppointmentsBeforeActivityStart: 0,
      form: {
        data: {
          facility: null,
          dispenser: null,
          activity: null,
        },
        errors: new Errors()
      },
      entitiesActivitiesUsers: [],
      selectedEntityActivityUser: null
    }
  },
  mounted: function () {
    this.isOpen = true
    this.loadEntityActivityUsersForFilters()
  },
  watch: {
    'selectedEntityActivityUser': {
      handler: function () {
        this.unmatchedAppointmentsAfterActivityEnd = this.selectedEntityActivityUser.end_date === null ? 0 : this.appointments.filter(app => this.moment(app.start).isAfter(this.moment(this.selectedEntityActivityUser.end_date))).length
        this.unmatchedAppointmentsBeforeActivityStart = this.selectedEntityActivityUser.start_date === null ? 0 : this.appointments.filter(app => this.moment(app.start).isBefore(this.moment(this.selectedEntityActivityUser.start_date))).length
      },
    },
    'form.data.facility': function (value) {
      if (!value) {
        this.form.data.activity = null
        this.selectedEntityActivityUser = null
      }
    },
    'form.data.activity': function (value) {
      if (value === null) {
        this.selectedEntityActivityUser = null
      }
    },
    entityActivityUserOptions: function (value) {
      if (this.selectedEntityActivityUser !== null && !value.some(entityActivityUser => entityActivityUser.id === this.selectedEntityActivityUser.id)) {
        this.selectedEntityActivityUser = null
      }
    }
  },
  methods: {
    handleShowConfirmationModal: function () {
      this.isOpen = false
      setTimeout(() => {
        this.showConfirmationModal = true
      }, 500)
    },
    select: function (entityActivityUser) {
      this.selectedEntityActivityUser = entityActivityUser
    },
    onCancel: function () {
      this.isOpen = false
      setTimeout(() => {
        this.$emit('modal-cancel')
      }, 500)
    },
    onConfirmationModalConfirmed: function () {
      this.showConfirmationModal = false
      this.updateAppointments()
    },
    onConfirmationModalCanceled: function () {
      this.isOpen = true
      this.showConfirmationModal = false
    },
    loadEntityActivityUsersForFilters: function () {
      this.isLoading = true

      if (this.isLoading) {
        let params = {
          infoType: appointmentsInfoType,
          serviceId: this.component.service.id,
          start: this.$options.filters.utcTime(this.startDate),
          end: this.$options.filters.utcTime(this.endDate),
          accessType: adminAccessType,
          activities: [this.activities.DISPENSER_FIELD, this.activities.DISPENSER_PREMISES].join(','),
          inclusive: false
        }

        if (this.form.data.facility !== null) {
          params.entities = this.writeEntityActivityUsers.find(elem => elem.entity.facilities.some(facility => facility.id === this.form.data.facility.id)).entity.id
        }

        if (this.form.data.activity !== null) {
          params.activities = this.form.data.activity.id
        }

        ActivityService.getActivityUsersByEntity(params)
          .then(entityActivityUsers => {
            this.writeEntityActivityUsers = entityActivityUsers.filter(eau => this.adminableAppointmentEntities.some(entity => entity.id === eau.entity.id))
          }).catch(errors => {
            console.log(errors)
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    updateAppointments: function () {
      let params = {
        start_date: this.startDate,
        end_date: this.endDate,
        facilities: this.selectedFacilities.map(facility => facility.id),
        activities: this.selectedActivities.map(activity => activity.id),
        users: this.selectedUsers.map(user => user.id),
        entity_activity_user_id: this.selectedEntityActivityUser.id,
        facility_id: this.form.data.facility.id
      }

      AppointmentService.appointmentsBulkUpdateActivity(this.component.service.url, params)
        .then(response => {
          this.showToast(this.$t('clinical.appointments.bulk_transfer_appointments_success_count', { count: response }), this.$store.state.successToastOptions)
        })
        .catch(err => {
          console.log(err)
          this.showToast(this.$t('clinical.appointments.something_went_wrong'), this.$store.state.errorToastOptions)
        })
        .finally(() => {
          this.$emit('appointments-updated')
        })
    }
  }
}
</script>
